import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import { useNavigate } from 'react-router';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Skeleton, 
  Stack
} from '@mui/material';
import { SvgIcon } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Storage from 'src/utils/storage/storage';
import { getAnnouncementMapping, setActiveBottomTab } from '../../../store/landing-slice';
import './section.scss';
import { Announcement } from '../../../types/landing-api-response.model';
import SnackbarComponent from 'src/pages/analytics/simulator/innovation/components/snackbar/snackbar';
import { AlertColor } from '@mui/material/Alert';
import { videoTutorialLink } from '../constant';

export default function AnnouncementsSection(parentHeightValue) {
  const appDispatch = useAppDispatch();

  const navigate = useNavigate();

  const {isAnnouncementLoading, announcementData} = useAppSelector(
    (state) => state.landing
  );

  // getting the source id from storage
  const { source: sourceId } = Storage.getSourceId();
  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: 'Announcement details not found',
    snackbarType: 'error'
  });

  useEffect(()=>{
    if(sourceId){
      appDispatch(getAnnouncementMapping({ sourceId }));
    }
  }, [sourceId]);

  // Pin Icon
  const PinIcon = (props: any) => (
    <SvgIcon {...props}>
      <path
        d='M8.06635 0.983521L13.0165 5.93369L12.1911 6.75852L11.7787 6.3461L9.30418 8.8206L8.89177 10.8833L8.06635 11.7081L5.59185 9.23302L2.70435 12.1205L1.87952 11.2957L4.76702 8.40819L2.29193 5.93369L3.11677 5.10827L5.18002 4.69585L7.65452 2.22135L7.2421 1.80894L8.06693 0.983521H8.06635ZM8.47935 3.04619L5.7546 5.77035L4.10843 6.09994L7.9001 9.8916L8.2291 8.24544L10.9538 5.52127L8.47935 3.04619Z'
        fill='#3A3A3A'
      />
    </SvgIcon>
  );
  
  // mapping for the necessary bottom window tabs, so that user when clicks on an announcement any one of these tabs can open
  const bottomWindowMapping = {
    "tab-0": "Quick Start",
    "tab-1": "Product Information",
    "tab-2": "Video Tutorials",
    "tab-3": "Contact Support"
  };

  // mapping for the necessary bottom window tabs, pbi reports
  const typesMapping = {
    "feature": "Product Information",
    "report": "pbi route_url",
  }

  const loadingSkeleton = () => {
    const a = [1, 1, 1, 1];
    return (
      <>
        {a.map((e) => (
          <Stack spacing={1} margin={1}>
            <Skeleton
              animation='wave'
              variant='rectangular'
              width='100%'
              height={30}
            />
          </Stack>
        ))}
      </>
    );
  };

  const handleRedirect = (announcement: Announcement) => {
    if(announcement.has_link && announcement.is_internal_link){
      // if pbi route_url is present then it navigates to that report
      if(announcement.type){
        const isValidType = Object.keys(typesMapping).filter((item) => item === announcement.type)
        if(isValidType[0] === "feature"){
           appDispatch(setActiveBottomTab(announcement.link))
        }else if(isValidType[0] === "report"){
           navigate(announcement.link)
        }else{
           setSnackbar(true) //to show error message 
        }
      }
      // to open the respective bottom tab when the announcement is having the link as the required tab name
      const bottomWindowToOpen = Object.values(bottomWindowMapping).filter(
        (item) => item.toLowerCase() === announcement.link.toLowerCase()
      )
      if(bottomWindowToOpen.length){
        const tabWindow = bottomWindowToOpen[0]
        tabWindow === 'Video Tutorials' ? window.open(videoTutorialLink, "_blank") : appDispatch(setActiveBottomTab(tabWindow))
      }
    }  
    else if(announcement.is_internal_link === false && announcement.has_link === true) {
      // if external url is present then open the link in a new window
      if(announcement.link){
        window.open(announcement.link, "_blank")
      }else{
        setSnackbar(true) //to show error message 
      }
    }
  }

  return (
    <Grid container className='announcements-container'>
      <div className='title-holder'>
        <img
          src='/static/images/Megaphones.png'
          style={{ height: '14px', width: '14px' }}
        ></img>
        <div className='announcement-title'>Announcements and Updates</div>
      </div>

      <div className='divider-div'></div>
    
      {/*loading skeleton while fetching the list of announcements */}
      {
        isAnnouncementLoading && loadingSkeleton()
      }
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
      {/* Pinned Announcement */}
      {!isAnnouncementLoading && announcementData && announcementData.length > 0 &&
      <>
      <div className='pinned-announcements'>
        <List>
          {announcementData
          .filter((announcement) => announcement.is_pinned)
          .map((announcement, index) => (
              <ListItem key={announcement.announcement_name} className={announcement.has_link ? 'items-list-has-link': 'items-list'} onClick={() => handleRedirect(announcement)}>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 'auto',
                      marginRight: '8px',
                      backgroundColor: `${announcement.color}`,
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%'
                    }}
                  />
                  <ListItemText sx={{marginTop: '0px'}}>
                    <span className='list-item-text'>
                      {announcement.short_description}
                    </span>
                  </ListItemText>
                  { announcement.has_link && <KeyboardArrowRightIcon sx={{ color: '#1E1E1E', fontSize: '16px' }} /> }
                  <PinIcon sx={{ margin: '8px 0px 0px 16px' }} />
                </div>
              </ListItem>
          ))}
        </List>
      </div>
      <div className='divider-div'></div>
      {/* Scrollable List of Other Announcements */}
      <div className='other-announcements'>
        <List>
          {announcementData
          .filter((announcement) => !announcement.is_pinned)
          .map((announcement, index) => (
              <ListItem key={announcement.announcement_name} className={announcement.has_link ? 'items-list-has-link': 'items-list'} onClick={() => handleRedirect(announcement)}>
                <ListItemIcon
                  sx={{
                    minWidth: 'auto',
                    marginRight: '8px',
                    backgroundColor: `${announcement.color}`,
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%'
                  }}
                />
                <ListItemText sx={{marginTop: '0px'}}>
                  <span className='list-item-text'>
                    {announcement.short_description}
                  </span>
                </ListItemText>
                { announcement.has_link && <KeyboardArrowRightIcon sx={{ color: '#1E1E1E', fontSize: '16px' }} /> }
              </ListItem>
          ))}
        </List>
      </div>
      </>
      }
    </Grid>
  );
}
